import type { CanvasTexture } from 'three';
import { canvasTexture } from '../../render/materials/canvasTexture';
import type { Livery } from './types';

const W = 256;

function plate(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, yellow: boolean): void {
  ctx.fillStyle = yellow ? '#f7d046' : '#f4f4f4';
  ctx.fillRect(x - 52, y - 14, 104, 28);
  ctx.strokeStyle = '#111';
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 52, y - 14, 104, 28);
  ctx.fillStyle = '#111';
  ctx.font = 'bold 17px "Arial Narrow", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y + 1, 96);
}

/** Windscreen, with an optional "GOD IS KING" style banner across the top. */
export function paintWindscreen(livery: Livery): CanvasTexture {
  return canvasTexture(W, 160, (ctx) => {
    const glass = ctx.createLinearGradient(0, 0, 0, 160);
    glass.addColorStop(0, '#3c5566');
    glass.addColorStop(1, '#0f171e');
    ctx.fillStyle = glass;
    ctx.fillRect(0, 0, W, 160);
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fillRect(30, 40, 26, 120);
    if (livery.frontBand) {
      ctx.fillStyle = '#b5121b';
      ctx.fillRect(0, 0, W, 34);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px Impact, "Arial Black", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(livery.frontBand, W / 2, 26, W - 16);
    }
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 8;
    ctx.strokeRect(0, 0, W, 160);
  });
}

/** Flat back: rear window with sticker, tail lights, plate. heightPx is proportional to the vehicle's height. */
export function paintRear(livery: Livery, windowTop: number, windowBottom: number, height: number, yellowPlate: boolean): CanvasTexture {
  const H = Math.round((W * height) / 2);
  const y = (v: number): number => ((height - v) / height) * H;
  return canvasTexture(W, H, (ctx) => {
    ctx.fillStyle = livery.body;
    ctx.fillRect(0, 0, W, H);
    for (const stripe of livery.stripes ?? []) {
      ctx.fillStyle = stripe.color;
      ctx.fillRect(0, y(stripe.v1), W, y(stripe.v0) - y(stripe.v1));
    }
    if (windowTop > windowBottom + 0.05) {
      ctx.fillStyle = '#121a21';
      ctx.beginPath();
      ctx.roundRect(18, y(windowTop), W - 36, y(windowBottom) - y(windowTop), 10);
      ctx.fill();
      if (livery.rearText) {
        ctx.fillStyle = '#ffd166';
        ctx.font = 'bold 22px Impact, "Arial Black", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(livery.rearText, W / 2, y(windowBottom) - 12, W - 50);
      }
    }
    ctx.fillStyle = '#d00000';
    ctx.fillRect(6, y(1.25), 22, y(0.75) - y(1.25));
    ctx.fillRect(W - 28, y(1.25), 22, y(0.75) - y(1.25));
    ctx.fillStyle = '#1b1b1b';
    ctx.fillRect(0, y(0.6), W, y(0.38) - y(0.6));
    if (livery.plate) plate(ctx, livery.plate, W / 2, y(0.75), yellowPlate);
  });
}

/** Nose: grille, headlights, bumper, plate. */
export function paintFront(livery: Livery, yellowPlate: boolean): CanvasTexture {
  return canvasTexture(W, 128, (ctx) => {
    ctx.fillStyle = livery.body;
    ctx.fillRect(0, 0, W, 128);
    ctx.fillStyle = '#222';
    ctx.fillRect(70, 20, W - 140, 40);
    ctx.fillStyle = '#f5f3e7';
    ctx.fillRect(10, 22, 52, 30);
    ctx.fillRect(W - 62, 22, 52, 30);
    ctx.fillStyle = '#1b1b1b';
    ctx.fillRect(0, 86, W, 42);
    if (livery.plate) plate(ctx, livery.plate, W / 2, 100, yellowPlate);
  });
}
